// F-536 (URS_12.3) — the 30/60/90-day questionnaire document the kernel issues with a survey,
// and the pure logic the Surveys screen needs to render and submit it.
//
// ⛔ NO QUESTION WORDING LIVES IN THIS APP. The approved, clinical wording is seeded in the kernel
// (`survey_instrument`, migration 0303) and arrives on `survey.questions`; everything here
// renders whatever that document says, verbatim. The kernel validates the answers and computes
// the flags — this module only shapes what the patient chose.

import type { Survey } from "../../services/engagement";

export interface InstrumentOption {
  code: string;
  label: string;
  flag?: boolean;
  creates_task?: boolean;
  /** "if yes, how many times?" — an optional whole number asked with this option. */
  count?: { prompt: string; min?: number; max?: number };
  /** "Other:" / "Yes: ___ (optional short comment)" — optional text with this option. */
  free_text?: { hint?: string };
}

export interface InstrumentQuestion {
  id: string;
  /** `null` for the opening "Who is completing this survey?" item, which is not numbered. */
  number: number | null;
  prompt: string;
  type: "single_choice" | "multi_choice";
  options: InstrumentOption[];
}

export interface Instrument {
  code: string;
  title: string;
  questions: InstrumentQuestion[];
}

/** One question's state while the patient is answering. */
export interface DraftAnswer {
  codes: string[];
  count: string;
  text: string;
}

export type Draft = Record<string, DraftAnswer>;

/** The instrument a survey was issued with, or `null` for a survey issued before F-536. */
export const instrumentOf = (survey: Pick<Survey, "questions">): Instrument | null => {
  const doc = survey.questions as Partial<Instrument> | null | undefined;
  return doc && Array.isArray(doc.questions) && doc.questions.length > 0
    ? (doc as Instrument)
    : null;
};

export const emptyDraft = (): DraftAnswer => ({ codes: [], count: "", text: "" });

/** Tap an option: a single-choice question swaps its answer, a multi-choice one toggles. */
export const choose = (draft: Draft, question: InstrumentQuestion, code: string): Draft => {
  const current = draft[question.id] ?? emptyDraft();
  const codes =
    question.type === "multi_choice"
      ? current.codes.includes(code)
        ? current.codes.filter((c) => c !== code)
        : [...current.codes, code]
      : [code];
  return { ...draft, [question.id]: { ...current, codes } };
};

const chosenOptions = (question: InstrumentQuestion, answer: DraftAnswer | undefined) =>
  question.options.filter((option) => answer?.codes.includes(option.code));

/** The option offering a count / a comment that the patient has chosen, if any. */
export const countOption = (question: InstrumentQuestion, answer: DraftAnswer | undefined) =>
  chosenOptions(question, answer).find((option) => option.count);
export const textOption = (question: InstrumentQuestion, answer: DraftAnswer | undefined) =>
  chosenOptions(question, answer).find((option) => option.free_text);

/** Whether a typed count is acceptable — blank is (it is optional), otherwise a whole number
 *  inside the option's own bounds. */
export const countIsValid = (option: InstrumentOption | undefined, raw: string): boolean => {
  if (!option?.count || raw.trim() === "") return true;
  if (!/^\d+$/.test(raw.trim())) return false;
  const value = Number(raw.trim());
  return value >= (option.count.min ?? 0) && value <= (option.count.max ?? Number.MAX_SAFE_INTEGER);
};

/** Every question answered (no open text is ever required) and every count well-formed. */
export const isComplete = (instrument: Instrument, draft: Draft): boolean =>
  instrument.questions.every((question) => {
    const answer = draft[question.id];
    return (
      !!answer &&
      answer.codes.length > 0 &&
      countIsValid(countOption(question, answer), answer.count)
    );
  });

/** The kernel's answer shape: `{id: {choice | choices, count?, text?}}`, options in the
 *  document's order. A count or comment is sent only with the option that asks for it. */
export const toAnswers = (instrument: Instrument, draft: Draft): Record<string, unknown> => {
  const answers: Record<string, unknown> = {};
  instrument.questions.forEach((question) => {
    const answer = draft[question.id] ?? emptyDraft();
    const codes = question.options
      .map((option) => option.code)
      .filter((code) => answer.codes.includes(code));
    const entry: Record<string, unknown> =
      question.type === "multi_choice" ? { choices: codes } : { choice: codes[0] };
    if (countOption(question, answer) && answer.count.trim() !== "") {
      entry.count = Number(answer.count.trim());
    }
    if (textOption(question, answer) && answer.text.trim() !== "") {
      entry.text = answer.text.trim();
    }
    answers[question.id] = entry;
  });
  return answers;
};
